import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, TrendingDown, Activity, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const deploymentTrend = [
  { day: 'Mon', deployments: 12, successful: 11, failed: 1 },
  { day: 'Tue', deployments: 8, successful: 8, failed: 0 },
  { day: 'Wed', deployments: 15, successful: 14, failed: 1 },
  { day: 'Thu', deployments: 10, successful: 9, failed: 1 },
  { day: 'Fri', deployments: 18, successful: 17, failed: 1 },
  { day: 'Sat', deployments: 4, successful: 4, failed: 0 },
  { day: 'Sun', deployments: 3, successful: 3, failed: 0 },
];

const responseTimeData = [
  { hour: '00:00', time: 120 },
  { hour: '04:00', time: 95 },
  { hour: '08:00', time: 145 },
  { hour: '12:00', time: 180 },
  { hour: '16:00', time: 165 },
  { hour: '20:00', time: 130 },
];

const environmentBreakdown = [
  { name: 'Production', value: 45, color: '#39FF14' },
  { name: 'Staging', value: 35, color: '#B24BF3' },
  { name: 'Development', value: 20, color: '#00F0FF' },
];

const deployTimeData = [
  { day: 'Mon', time: 78 },
  { day: 'Tue', time: 92 },
  { day: 'Wed', time: 65 },
  { day: 'Thu', time: 88 },
  { day: 'Fri', time: 72 },
  { day: 'Sat', time: 45 },
  { day: 'Sun', time: 52 },
];

export default function AnalyticsPage() {
  const stats = [
    { title: 'Total Deployments', value: '247', change: '+18%', trend: 'up', icon: Activity },
    { title: 'Success Rate', value: '98.2%', change: '+2.1%', trend: 'up', icon: CheckCircle },
    { title: 'Avg Deploy Time', value: '72s', change: '-12s', trend: 'down', icon: Clock },
    { title: 'Incidents', value: '3', change: '-40%', trend: 'down', icon: AlertCircle },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white" data-testid="analytics-title">
            Analytics
          </h1>
          <p className="text-foreground/60">Deployment metrics and performance insights</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="bg-card/50 border-border">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="font-heading text-3xl font-bold text-white">{stat.value}</p>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${
                        stat.trend === 'up' ? 'text-accent' : 'text-primary'
                      }`}>
                        {stat.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        <span>{stat.change}</span>
                      </div>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <stat.icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deployment Trend */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Deployment Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" style={{ width: '100%', minHeight: '256px' }}>
                  <ResponsiveContainer width="100%" height={256}>
                    <BarChart data={deploymentTrend}>
                      <XAxis dataKey="day" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#151929', 
                          border: '1px solid #1F294F',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="successful" fill="#39FF14" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="failed" fill="#FF10F0" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Response Time */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-secondary" />
                  Response Time (ms)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" style={{ width: '100%', minHeight: '256px' }}>
                  <ResponsiveContainer width="100%" height={256}>
                    <AreaChart data={responseTimeData}>
                      <defs>
                        <linearGradient id="responseGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#B24BF3" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#B24BF3" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="hour" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#151929', 
                          border: '1px solid #1F294F',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="time" 
                        stroke="#B24BF3" 
                        fill="url(#responseGradient)" 
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Environment Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white">Environment Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center" style={{ width: '100%', minHeight: '256px' }}>
                  <ResponsiveContainer width="100%" height={256}>
                    <PieChart>
                      <Pie
                        data={environmentBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {environmentBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#151929', 
                          border: '1px solid #1F294F',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-4">
                  {environmentBreakdown.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-xs text-muted-foreground">{item.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Deploy Time Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="lg:col-span-2"
          >
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-accent" />
                  Average Deploy Time (seconds)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" style={{ width: '100%', minHeight: '256px' }}>
                  <ResponsiveContainer width="100%" height={256}>
                    <LineChart data={deployTimeData}>
                      <XAxis dataKey="day" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#151929', 
                          border: '1px solid #1F294F',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="time" 
                        stroke="#00F0FF" 
                        strokeWidth={2}
                        dot={{ fill: '#00F0FF', strokeWidth: 0 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
